import React, { useEffect, useRef } from 'react';

interface Model3DViewerProps {
  modelSrc: string; // مسار ملف GLB/GLTF
  iosSrc?: string; // مسار ملف USDZ لأجهزة Apple
  alt: string;
  poster?: string; // صورة تظهر أثناء التحميل
}

function Model3DViewer({ modelSrc, iosSrc, alt, poster }: Model3DViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // التحقق من وجود المكتبة
    const loadModelViewer = async () => {
      // تحميل المكتبة إذا لم تكن موجودة
      if (!customElements.get('model-viewer')) {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.3.0/model-viewer.min.js';
        
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      if (containerRef.current) {
        // مسح المحتوى السابق
        containerRef.current.innerHTML = '';
        
        // إنشاء عنصر model-viewer
        const modelViewer = document.createElement('model-viewer') as any;
        
        // إضافة الخصائص
        modelViewer.setAttribute('src', modelSrc);
        if (iosSrc) modelViewer.setAttribute('ios-src', iosSrc);
        modelViewer.setAttribute('alt', alt);
        if (poster) modelViewer.setAttribute('poster', poster);
        modelViewer.setAttribute('shadow-intensity', '1');
        modelViewer.setAttribute('camera-controls', '');
        modelViewer.setAttribute('auto-rotate', '');
        modelViewer.setAttribute('ar', '');
        modelViewer.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
        modelViewer.setAttribute('loading', 'eager');
        modelViewer.setAttribute('reveal', 'auto');
        
        // إضافة معالج الأخطاء
        modelViewer.addEventListener('error', (event: any) => {
          console.error('Model loading error:', event);
          console.error('Model source:', modelSrc);

          // التحقق من متصفح Instagram
          const isInstagram = /Instagram/.test(navigator.userAgent);

          // إظهار رسالة خطأ للمستخدم
          if (containerRef.current) {
            if (isInstagram) {
              // رسالة خاصة لمتصفح Instagram
              containerRef.current.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; height: 400px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 12px; box-shadow: 0 4px 15px rgba(0,0,0,0.2);">
                  <div style="text-align: center; color: #fff; padding: 30px; max-width: 500px;">
                    <div style="font-size: 64px; margin-bottom: 20px;">📱</div>
                    <div style="font-size: 22px; font-weight: bold; margin-bottom: 16px; line-height: 1.4;">عرض المجسم ثلاثي الأبعاد</div>
                    <div style="font-size: 16px; margin-bottom: 24px; line-height: 1.6; opacity: 0.95;">
                      متصفح Instagram لا يدعم عرض المجسمات ثلاثية الأبعاد.<br/>
                      يرجى فتح هذه الصفحة في متصفح جوالك (Chrome، Safari) لرؤية المجسم بشكل تفاعلي.
                    </div>
                    <div style="display: inline-block; background: rgba(255,255,255,0.2); padding: 12px 24px; border-radius: 8px; font-size: 14px; backdrop-filter: blur(10px);">
                      انقر على القائمة (⋯) واختر "فتح في المتصفح"
                    </div>
                  </div>
                </div>
              `;
            } else {
              // رسالة عامة للأخطاء الأخرى
              containerRef.current.innerHTML = `
                <div style="display: flex; align-items: center; justify-content: center; height: 400px; background: #f5f5f5; border: 2px dashed #ccc; border-radius: 8px;">
                  <div style="text-align: center; color: #666; padding: 20px;">
                    <div style="font-size: 48px; margin-bottom: 16px;">📦</div>
                    <div style="font-size: 18px; margin-bottom: 8px;">النموذج ثلاثي الأبعاد غير متوفر حالياً</div>
                    <small style="color: #999;">سيتم إضافة النموذج قريباً</small>
                  </div>
                </div>
              `;
            }
          }
        });
        
        // تحديد حركة الكاميرا
        modelViewer.setAttribute('disable-pan', ''); // منع تحريك النموذج من مكانه
        modelViewer.setAttribute('min-camera-orbit', 'auto 75deg auto'); // منع الدوران أسفل من 75 درجة
        modelViewer.setAttribute('max-camera-orbit', 'auto 105deg auto'); // منع الدوران أعلى من 105 درجة
        modelViewer.setAttribute('min-field-of-view', '25deg'); // الحد الأدنى للتقريب
        modelViewer.setAttribute('max-field-of-view', '45deg'); // الحد الأقصى للتبعيد
        modelViewer.setAttribute('interpolation-decay', '200'); // سلاسة الحركة
        
        // تحديد حجم النموذج حسب حجم الشاشة
        const isMobileScreen = window.innerWidth <= 768;
        if (isMobileScreen) {
          modelViewer.setAttribute('camera-orbit', '0deg 75deg 3m'); // موضع الكاميرا أبعد للشاشات الصغيرة
          modelViewer.setAttribute('scale', '0.45 0.45 0.45'); // حجم مثالي للموبايل
        } else {
          modelViewer.setAttribute('camera-orbit', '0deg 75deg 2.2m'); // موضع الكاميرا الافتراضي أقرب
          modelViewer.setAttribute('scale', '0.4 0.4 0.4'); // تصغير أكثر للشاشات الكبيرة
        }
        
        // تطبيق الأنماط
        modelViewer.style.width = '100%';
        modelViewer.style.height = '100%';
        modelViewer.style.minHeight = '400px';
        modelViewer.style.backgroundColor = 'transparent';

        // إنشاء زر AR للداخل (مخفي)
        const arButton = document.createElement('button');
        arButton.setAttribute('slot', 'ar-button');
        arButton.style.display = 'none';
        
        // إنشاء زر AR للخارج
        const externalArButton = document.createElement('button');
        externalArButton.className = 'ar-button-external';
        externalArButton.innerHTML = `
          <img src="/images/ar.svg" alt="AR" width="20" height="20" />
          عرض الواقع المعزز
        `;
        externalArButton.onclick = () => {
          arButton.click();
        };


        // إضافة رسالة مساعدة
        const helpText = document.createElement('div');
        helpText.className = 'model-help';
        const isMobile = window.innerWidth <= 768;
        helpText.innerHTML = isMobile ? 'استخدم إصبعك للتحريك والتدوير' : 'استخدم الماوس للتدوير والتكبير';
        helpText.style.transition = 'opacity 0.3s';
        helpText.style.opacity = '0';
        
        // إضافة مؤشر التحميل
        const loadingIndicator = document.createElement('div');
        loadingIndicator.className = 'model-loading';
        loadingIndicator.innerHTML = `
          <div class="loading-spinner"></div>
          <p>جاري تحميل النموذج...</p>
        `;

        modelViewer.appendChild(arButton);
        
        // إنشاء container نسبي للنموذج
        const modelContainer = document.createElement('div');
        modelContainer.style.position = 'relative';
        modelContainer.style.width = '100%';
        modelContainer.style.height = '100%';
        modelContainer.style.transform = isMobileScreen ? 'scale(1)' : 'scale(1.15)'; // حجم أصغر للشاشات الصغيرة
        modelContainer.style.transformOrigin = 'center center';
        modelContainer.appendChild(loadingIndicator);
        modelContainer.appendChild(modelViewer);
        
        containerRef.current.appendChild(modelContainer);
        
        // إخفاء مؤشر التحميل وإظهار رسالة المساعدة عند تحميل النموذج
        modelViewer.addEventListener('load', () => {
          loadingIndicator.style.display = 'none';
          modelContainer.appendChild(helpText);
          // إظهار الرسالة بتأثير fade in
          setTimeout(() => {
            helpText.style.opacity = '1';
            
            // إخفاء رسالة المساعدة بعد ثانيتين من ظهورها
            setTimeout(() => {
              if (helpText.parentNode) {
                helpText.style.opacity = '0';
                setTimeout(() => helpText.remove(), 300);
              }
            }, 2000);
          }, 100);
          
          // إضافة زر AR للخارج
          const arButtonContainer = document.getElementById('ar-button-container');
          if (arButtonContainer && !arButtonContainer.hasChildNodes()) {
            arButtonContainer.appendChild(externalArButton);
          }
        });
        
        // إظهار خطأ إذا فشل التحميل
        modelViewer.addEventListener('error', () => {
          loadingIndicator.innerHTML = `
            <p style="color: #ff6b6b;">فشل تحميل النموذج</p>
          `;
        });
      }
    };

    loadModelViewer().catch(console.error);

    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [modelSrc, iosSrc, alt, poster]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: '400px', position: 'relative' }} />;
}

export default Model3DViewer;